import { Autocomplete, FormControl, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAppDataContext } from '../../../../contexts/appData';
import { useLanguage } from '../../../../contexts/language';
import { VesselPartialFormProps } from '../types';
import { DAYJS_VISUALIZATION_DATE_FORMAT } from '../../../../utils/date';
import FormLayout, {
  FormLayoutInputElement,
} from '../../../../components/FormLayout';

const VesselTechnicalDetailsForm = ({
  formManager,
}: VesselPartialFormProps) => {
  const {
    formState,
    materialInputHelpers: { getBaseProps, getTextFieldProps, renderError },
  } = formManager;
  const { values, updateValues } = formState;
  const { t } = useLanguage();
  const { appData } = useAppDataContext();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormLayout>
        <FormLayoutInputElement
          small
          helperText={t('content.length-overall')}
          label={t('content.loa')}
        >
          <TextField
            {...getTextFieldProps('lengthOverall')}
            placeholder={t('content.loa')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          label={t('content.current-survey-status')}
        >
          <TextField
            {...getTextFieldProps('currentSurveyStatus')}
            placeholder={t('content.current-survey-status')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          helperText={t('content.maximum-width')}
          label={t('content.beam')}
        >
          <TextField
            {...getTextFieldProps('beam')}
            placeholder={t('content.beam')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          helperText={t('content.dry-docking-schedule-definition')}
          label={t('content.dry-docking-schedule')}
        >
          <TextField
            {...getTextFieldProps('dryDockingSchedule')}
            placeholder={t('content.dry-docking-schedule')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          helperText={t('content.load-draft-definition')}
          label={t('content.load-draft')}
        >
          <TextField
            {...getTextFieldProps('loadDraft')}
            placeholder={t('content.load-draft')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          helperText={t('content.date-of-last-dry-docking-definition')}
          label={t('content.date-of-last-dry-docking')}
        >
          <FormControl error={getBaseProps('dateOfLastDryDocking').error}>
            <DatePicker
              className="w-full"
              name={getBaseProps('dateOfLastDryDocking').name}
              disabled={getBaseProps('dateOfLastDryDocking').disabled}
              format={DAYJS_VISUALIZATION_DATE_FORMAT}
              slotProps={{
                textField: {
                  error: getBaseProps('dateOfLastDryDocking').error,
                  onBlur: getBaseProps('dateOfLastDryDocking').onBlur,
                },
              }}
              value={values.dateOfLastDryDocking}
              onChange={newValue =>
                updateValues({
                  dateOfLastDryDocking: newValue,
                })
              }
            />
            {renderError('dateOfLastDryDocking')}
          </FormControl>
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          helperText={t('content.horse-power-definition')}
          label={t('content.horse-power')}
        >
          <TextField
            {...getTextFieldProps('horsePower')}
            placeholder={t('content.horse-power')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          helperText={t('content.details-of-cargo-gear-definition')}
          label={t('content.details-of-cargo-gear')}
        >
          <TextField
            {...getTextFieldProps('detailsOfCargoGear')}
            placeholder={t('content.details-of-cargo-gear')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          helperText={t('content.service-speed-definition')}
          label={t('content.service-speed')}
        >
          <TextField
            {...getTextFieldProps('ratedEngineSpeed')}
            placeholder={t('content.service-speed')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          label={t('content.cargo-carriage-capacity')}
        >
          <TextField
            {...getTextFieldProps('cargoCapacity')}
            placeholder={t('content.cargo-carriage-capacity')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement small required label={t('content.fuel-type')}>
          <FormControl error={getBaseProps('fuelType').error}>
            <Autocomplete<string>
              {...getBaseProps('fuelType')}
              onChange={(_event, selectedOption) =>
                updateValues({ fuelType: selectedOption || '' }, true)
              }
              options={appData.fuelTypes}
              renderInput={params => (
                <TextField
                  {...params}
                  error={getBaseProps('fuelType').error}
                  placeholder={t('content.select')}
                />
              )}
              getOptionLabel={fuelType =>
                fuelType ? t(`fuel-types.${fuelType}`) : ''
              }
            />
            {renderError('fuelType')}
          </FormControl>
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          label={t('content.vessel-owner')}
        >
          <TextField
            {...getTextFieldProps('vesselOwner')}
            placeholder={t('content.vessel-owner')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          helperText={t('content.bunker-capacity-definition')}
          label={t('content.bunker-capacity')}
        >
          <TextField
            {...getTextFieldProps('bunkerCapacity')}
            placeholder={t('content.bunker-capacity')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          helperText={t('content.details-of-detentions-definition')}
          label={t('content.details-of-detentions')}
        >
          <TextField
            {...getTextFieldProps('detailsOfDetentions')}
            placeholder={t('content.details-of-detentions')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          label={t('content.average-fuel-consumption')}
        >
          <TextField
            {...getTextFieldProps('averageFuelConsumption')}
            placeholder={t('content.average-fuel-consumption')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          label={t('content.passenger-carriage-capacity')}
        >
          <TextField
            {...getTextFieldProps('passengerCarriageCapacity')}
            placeholder={t('content.passenger-carriage-capacity')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          helperText={t('content.manning-scale-definition')}
          label={t('content.manning-scale')}
        >
          <TextField
            {...getTextFieldProps('manningScale')}
            placeholder={t('content.manning-scale')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          label={t('content.average-crossing-time')}
        >
          <TextField
            {...getTextFieldProps('averageCrossingTime')}
            placeholder={t('content.average-crossing-time')}
          />
        </FormLayoutInputElement>
        <FormLayoutInputElement
          small
          required
          helperText={t('content.details-of-conversion-definition')}
          label={t('content.details-of-conversion')}
        >
          <TextField
            {...getTextFieldProps('detailsOfConversion')}
            placeholder={t('content.details-of-conversion')}
          />
        </FormLayoutInputElement>
      </FormLayout>
    </LocalizationProvider>
  );
};

export default VesselTechnicalDetailsForm;
